import { backgroundGet } from "@/api/base/private/backgroundClient";
import { loadJanusScript } from "@/src/lib/janus/loadJanusScript";
import { normalizeJanusServerUrl } from "@/app/assignments/[id]/components/ProctoringList/utils/janus";

export interface LiveRoom {
  assignmentId: number;
  assignmentName: string | null;
  webinarId: number | null;
  room: number | string;
  token: string | null;
  host: string;
}

export interface RoomSweep {
  assignmentId: number;
  assignmentName: string | null;
  webinarId: number | null;
  studentIds: number[];
}

const ROOMS_TTL_MS = 60_000;
const CONNECT_TIMEOUT_MS = 1500;
const REQUEST_TIMEOUT_MS = 3000;
const SESSION_IDLE_MS = 120_000;
const HOST_COOLDOWN_MS = 60_000;

const PUBLISHER_DISPLAY = /^student-(?:main-camera|second-camera|screen)-(\d+)$/;

interface RawRunningAssignment {
  id: number;
  name?: string | null;
  is_proctoring?: boolean | number | null;
  webinar?: {
    id?: number | null;
    room?: unknown;
    token?: unknown;
    server?: { host?: unknown } | null;
  } | null;
}

/**
 * Комнаты, о которых мы знаем НЕ из опроса, а из живого подключения страницы прокторинга.
 * Пока менеджер сидит в задании, его собственная комната известна мгновенно и точнее опроса —
 * именно она и должна побеждать, иначе палитра и точка на табе расходятся.
 */
const ROOM_STATE = new Map<number, { at: number; studentIds: number[] }>();
const ROOM_STATE_TTL_MS = 10_000;

const ROOM_LISTENERS = new Set<() => void>();

/**
 * Страница прокторинга узнаёт о включённой камере мгновенно, событием из комнаты.
 * Чтобы палитра и списки не ждали своего тика опроса, о смене состава сообщаем сразу.
 */
export function subscribeRoomState(listener: () => void): () => void {
  ROOM_LISTENERS.add(listener);
  return () => ROOM_LISTENERS.delete(listener);
}

export function publishRoomState(assignmentId: number, displays: string[]): void {
  const ids: number[] = [];

  for (const display of displays) {
    const match = PUBLISHER_DISPLAY.exec(String(display ?? ""));
    if (match) ids.push(Number(match[1]));
  }

  const next = [...new Set(ids)].sort((a, b) => a - b);
  const previous = ROOM_STATE.get(assignmentId);
  const changed =
    !previous ||
    previous.studentIds.length !== next.length ||
    previous.studentIds.some((id, index) => id !== next[index]);

  ROOM_STATE.set(assignmentId, { at: Date.now(), studentIds: next });

  if (changed) {
    for (const listener of ROOM_LISTENERS) listener();
  }
}

export function readRoomState(): Map<number, number[]> {
  const now = Date.now();
  const result = new Map<number, number[]>();

  for (const [assignmentId, entry] of ROOM_STATE) {
    if (now - entry.at > ROOM_STATE_TTL_MS) {
      ROOM_STATE.delete(assignmentId);
      continue;
    }

    result.set(assignmentId, entry.studentIds);
  }

  return result;
}

let roomsCache: { at: number; rooms: LiveRoom[] } | null = null;
let roomsInflight: Promise<LiveRoom[]> | null = null;

export async function fetchLiveRooms(): Promise<LiveRoom[]> {
  if (roomsCache && Date.now() - roomsCache.at < ROOMS_TTL_MS) return roomsCache.rooms;
  if (roomsInflight) return roomsInflight;

  roomsInflight = (async () => {
    const { data } = await backgroundGet("/assignments", {
      params: { status: "process,suspended", limit: 200, per_page: 200 },
      headers: {
        "X-Requested-Fields":
          "id,name,is_proctoring,webinar:id,webinar:room,webinar:token,webinar:server",
      },
    });

    const entities = (data as { entities?: unknown } | null)?.entities;
    const list = Array.isArray(entities)
      ? (entities as RawRunningAssignment[])
      : ((entities as { data?: RawRunningAssignment[] } | undefined)?.data ?? []);

    const rooms: LiveRoom[] = [];

    for (const item of list) {
      // Без прокторинга студент камеру не публикует — такую комнату и спрашивать незачем.
      // Фильтр на стороне сервера (`proctoring=1`) молча игнорируется, поэтому отсеиваем сами.
      if (!item.is_proctoring) continue;

      const webinar = item.webinar;
      const host = normalizeJanusServerUrl(webinar?.server?.host);
      const room = webinar?.room;

      if (!host || room == null || room === "") continue;

      rooms.push({
        assignmentId: item.id,
        assignmentName: item.name ?? null,
        webinarId: typeof webinar?.id === "number" ? webinar.id : null,
        room: room as number | string,
        token: typeof webinar?.token === "string" ? webinar.token : null,
        host,
      });
    }

    roomsCache = { at: Date.now(), rooms };
    return rooms;
  })().finally(() => {
    roomsInflight = null;
  });

  return roomsInflight;
}

export function dropLiveRoomsCache(): void {
  roomsCache = null;
}

interface HostSession {
  janus: any;
  handle: any;
  token: string | null;
  touchedAt: number;
}

const SESSIONS = new Map<string, HostSession>();
const CONNECTING = new Map<string, Promise<HostSession | null>>();
const COOLDOWN = new Map<string, number>();

function destroySession(host: string): void {
  const session = SESSIONS.get(host);
  if (!session) return;

  SESSIONS.delete(host);

  try {
    session.janus.destroy({ unload: true, notifyDestroyed: false });
  } catch {
    // соединение уже мертво — молча отпускаем
  }
}

let readers = 0;

/**
 * Соединения живут, пока их кто-то читает: палитра, список заданий, карточка.
 * Последний ушедший гасит свет — иначе закрытая палитра рвала бы соединение,
 * которым в этот момент пользуется список.
 *
 * Кулдаун недоступного видеосервера при этом НЕ сбрасываем: иначе каждое открытие
 * снова платило бы таймаутом соединения с мёртвым хостом.
 */
export function acquireJanusSweep(): () => void {
  readers += 1;

  let released = false;

  return () => {
    if (released) return;
    released = true;

    readers = Math.max(0, readers - 1);

    if (readers === 0) closeJanusSweep();
  };
}

export function closeJanusSweep(): void {
  for (const host of [...SESSIONS.keys()]) destroySession(host);
}

async function openSession(host: string, token: string | null): Promise<HostSession | null> {
  const ready = await loadJanusScript();
  if (!ready) return null;

  const Janus = (window as unknown as { Janus?: any }).Janus;
  if (!Janus) return null;

  if (!Janus.initDone) {
    await new Promise<void>((resolve) => {
      Janus.init({ debug: false, callback: () => resolve() });
    });
  }

  const janus = await new Promise<any>((resolve) => {
    let settled = false;
    const done = (value: any) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const instance = new Janus({
      server: host,
      token,
      success: () => done(instance),
      error: () => done(null),
      destroyed: () => SESSIONS.delete(host),
    });

    setTimeout(() => done(null), CONNECT_TIMEOUT_MS);
  });

  if (!janus) return null;

  const handle = await new Promise<any>((resolve) => {
    let settled = false;
    const done = (value: any) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    janus.attach({
      plugin: "janus.plugin.videoroom",
      success: (pluginHandle: unknown) => done(pluginHandle),
      error: () => done(null),
    });

    setTimeout(() => done(null), CONNECT_TIMEOUT_MS);
  });

  if (!handle) {
    try {
      janus.destroy({ unload: true, notifyDestroyed: false });
    } catch {
      // всё равно уходим
    }
    return null;
  }

  return { janus, handle, token, touchedAt: Date.now() };
}

async function ensureSession(host: string, token: string | null): Promise<HostSession | null> {
  const existing = SESSIONS.get(host);

  if (existing) {
    existing.touchedAt = Date.now();
    return existing;
  }

  const cooldownUntil = COOLDOWN.get(host);

  if (cooldownUntil != null) {
    if (Date.now() < cooldownUntil) return null;
    COOLDOWN.delete(host);
  }

  const pending = CONNECTING.get(host);
  if (pending) return pending;

  const run = openSession(host, token)
    .then((session) => {
      if (session) {
        SESSIONS.set(host, session);
        COOLDOWN.delete(host);
      } else {
        COOLDOWN.set(host, Date.now() + HOST_COOLDOWN_MS);
      }

      return session;
    })
    .catch(() => null)
    .finally(() => {
      CONNECTING.delete(host);
    });

  CONNECTING.set(host, run);
  return run;
}

interface Participant {
  display?: string | null;
  publisher?: boolean;
}

interface RoomListEntry {
  room?: number | string;
  num_participants?: number;
}

const sweepDbgOn = (): boolean => {
  try {
    return (
      typeof window !== "undefined" &&
      (window.location.search.indexOf("roomDebug=1") !== -1 ||
        window.localStorage.getItem("roomDebug") === "1")
    );
  } catch {
    return false;
  }
};

const LIST_UNSUPPORTED = new Set<string>();

function listRoomOccupancy(
  session: HostSession,
  host: string
): Promise<Map<string, number> | null> {
  if (LIST_UNSUPPORTED.has(host)) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const done = (value: Map<string, number> | null) => {
      if (settled) return;
      settled = true;
      if (value === null) LIST_UNSUPPORTED.add(host);
      resolve(value);
    };

    session.handle.send({
      message: { request: "list" },
      success: (response: { list?: RoomListEntry[]; error?: string }) => {
        if (response?.error || !Array.isArray(response?.list)) return done(null);

        const occupancy = new Map<string, number>();

        for (const entry of response.list) {
          if (entry?.room == null) continue;
          occupancy.set(String(entry.room), Number(entry.num_participants ?? 0) || 0);
        }

        done(occupancy.size > 0 ? occupancy : null);
      },
      error: () => done(null),
    });

    setTimeout(() => done(null), REQUEST_TIMEOUT_MS);
  });
}

function askRoom(session: HostSession, room: number | string): Promise<number[] | null> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (value: number[] | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    session.handle.send({
      message: { request: "listparticipants", room },
      success: (response: { participants?: Participant[]; error?: string }) => {
        if (response?.error) return done(null);

        const ids: number[] = [];

        for (const participant of response?.participants ?? []) {
          if (participant?.publisher === false) continue;

          const match = PUBLISHER_DISPLAY.exec(String(participant?.display ?? ""));
          if (match) ids.push(Number(match[1]));
        }

        done([...new Set(ids)]);
      },
      error: () => done(null),
    });

    setTimeout(() => done(null), REQUEST_TIMEOUT_MS);
  });
}

export async function sweepLiveRooms(rooms: LiveRoom[]): Promise<RoomSweep[] | null> {
  if (rooms.length === 0) return [];

  const byHost = new Map<string, LiveRoom[]>();

  for (const room of rooms) {
    const bucket = byHost.get(room.host) ?? [];
    bucket.push(room);
    byHost.set(room.host, bucket);
  }

  const result: RoomSweep[] = [];
  let hostsUp = 0;

  await Promise.all(
    [...byHost.entries()].map(async ([host, hostRooms]) => {
      const token = hostRooms.find((item) => item.token)?.token ?? null;
      const session = await ensureSession(host, token);

      if (!session) {
        return;
      }

      hostsUp += 1;

      const occupancy = await listRoomOccupancy(session, host);

      const asked = occupancy
        ? hostRooms.filter((room) => (occupancy.get(String(room.room)) ?? 0) > 0)
        : hostRooms;

      if (sweepDbgOn()) {
        console.log(
          `[sweep] ${host} list=${occupancy ? "ok" : "нет"} комнат=${hostRooms.length} спрошено=${asked.length}`
        );
      }

      const answers = await Promise.all(
        asked.map(async (room) => ({ room, ids: await askRoom(session, room.room) }))
      );

      const dead = answers.filter((item) => item.ids === null).length;

      if (answers.length > 0 && dead === answers.length) {
        destroySession(host);
        COOLDOWN.set(host, Date.now() + HOST_COOLDOWN_MS);
        return;
      }

      for (const { room, ids } of answers) {
        if (!ids || ids.length === 0) continue;

        result.push({
          assignmentId: room.assignmentId,
          assignmentName: room.assignmentName,
          webinarId: room.webinarId,
          studentIds: ids,
        });
      }
    })
  );

  if (hostsUp === 0) return null;

  return result;
}

export async function sweepAssignment(assignmentId: number): Promise<Set<number> | null> {
  const rooms = await fetchLiveRooms().catch(() => [] as LiveRoom[]);
  const room = rooms.find((item) => item.assignmentId === assignmentId);

  if (!room) return null;

  const result = await sweepLiveRooms([room]).catch(() => null);

  if (result === null) return null;

  return new Set(result.flatMap((item) => item.studentIds));
}

export function releaseIdleSweepSessions(): void {
  const now = Date.now();

  for (const [host, session] of SESSIONS) {
    if (now - session.touchedAt > SESSION_IDLE_MS) destroySession(host);
  }
}
